import React, { useEffect, useRef, useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';

/**
 * DecisionPanel — แผงอนุมัติ/ปฏิเสธของหน้าตรวจคำขอ
 *
 * ข้อบังคับจาก FR-ADM-09 (ปฏิเสธต้องระบุเหตุผล) และ FR-ADM-04 (ปรับคะแนนต้องระบุเหตุผล):
 * ปุ่มยืนยันการปฏิเสธถูกปิดไว้จนกว่าจะเลือกเหตุผลสำเร็จรูปหรือพิมพ์เหตุผลอย่างน้อย 10 ตัวอักษร
 *
 * เหตุผลนี้ไปถึงลูกค้าจริง — เหตุผลสำเร็จรูปจึงต้องเขียนให้ลูกค้าอ่านรู้เรื่อง
 * ไม่ใช่ 'ไม่ผ่านเงื่อนไข' (ดู README กติกาข้อความ)
 *
 * เป้าหมายที่วัดได้: รายการที่ยอดถูกต้อง = การกด 1 ครั้ง (ปุ่มอนุมัติ หรือคีย์ A)
 */
export interface RejectReason {
  code: string;
  /** ข้อความที่ลูกค้าจะได้เห็น */
  label: string;
  /** ต้องพิมพ์รายละเอียดเพิ่มหรือไม่ */
  requiresDetail?: boolean;
}

export interface DecisionPanelProps {
  /** สรุปสิ่งที่กำลังตัดสิน เช่น ยอดที่ลูกค้ากรอกกับยอดที่เจ้าหน้าที่อ่านได้ */
  summary?: React.ReactNode;
  onApprove: () => void;
  onReject: (payload: { code: string; label: string; detail: string }) => void;
  reasons: RejectReason[];
  /** ปิดปุ่มระหว่างส่งคำสั่ง */
  busy?: boolean;
  /** ข้อความเตือนที่ต้องอ่านก่อนอนุมัติ เช่น 'ระบบพบสลิปนี้ซ้ำกับคำขอ #12841' */
  warning?: React.ReactNode;
  /** ผลที่จะเกิดหลังอนุมัติ เช่น 'ลูกค้าจะได้รับ 240 คะแนน และเลื่อนเป็นระดับ Gold' */
  approveConsequence?: React.ReactNode;
  /** ปิดคีย์ลัด A/R (เช่น เมื่อมี modal อื่นเปิดอยู่) */
  shortcutsEnabled?: boolean;
  className?: string;
}

const MIN_DETAIL = 10;

export const DecisionPanel: React.FC<DecisionPanelProps> = ({
  summary, onApprove, onReject, reasons, busy = false,
  warning, approveConsequence, shortcutsEnabled = true, className,
}) => {
  const [rejecting, setRejecting] = useState(false);
  const [code, setCode] = useState('');
  const [detail, setDetail] = useState('');
  const firstReasonRef = useRef<HTMLInputElement>(null);

  const chosen = reasons.find((r) => r.code === code) ?? null;
  const needsDetail = chosen?.requiresDetail ?? false;
  const canSubmit = Boolean(chosen) && (!needsDetail || detail.trim().length >= MIN_DETAIL);

  useEffect(() => { if (rejecting) firstReasonRef.current?.focus(); }, [rejecting]);

  useKeyboardShortcuts([
    { key: 'a', description: 'อนุมัติรายการที่เปิดอยู่', group: 'การตัดสิน', handler: () => { if (!busy && !rejecting) onApprove(); } },
    { key: 'r', description: 'ปฏิเสธ — เปิดช่องเหตุผล (บังคับกรอก)', group: 'การตัดสิน', handler: () => setRejecting(true) },
    { key: 'Escape', description: 'ยกเลิกการปฏิเสธ', group: 'การตัดสิน', handler: () => setRejecting(false), disabled: !rejecting, allowInInput: true },
  ], shortcutsEnabled);

  return (
    <section
      aria-label="การตัดสินคำขอ"
      className={cn(
        'flex flex-col gap-[var(--space-3)] border-t border-[var(--line-200)] bg-[var(--surface-0)] p-[var(--space-4)] font-[family-name:var(--font-thai)] shadow-[var(--elevation-sticky)]',
        className,
      )}
    >
      {summary}

      {warning && (
        <div role="alert" className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-sm)] border border-[var(--warning-border)] bg-[var(--warning-bg)] p-[var(--space-25)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--warning-text)]">
          <Icon name="warning" size={18} filled />
          <div>{warning}</div>
        </div>
      )}

      {!rejecting ? (
        <div className="flex flex-wrap items-center gap-[var(--space-2)]">
          <button
            type="button"
            disabled={busy}
            onClick={onApprove}
            className="inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] bg-[var(--success-solid)] px-[var(--space-5)] text-[length:var(--text-form-lg)] font-[var(--weight-semibold)] text-white hover:bg-[var(--success-text)] disabled:opacity-60 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
          >
            <Icon name="check_circle" size={20} filled />
            อนุมัติ
            <kbd className="ml-[var(--space-1)] rounded-[var(--radius-xs)] bg-white/25 px-1 font-[family-name:var(--font-num)] text-[length:var(--text-kbd)]">A</kbd>
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={() => setRejecting(true)}
            className="inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] border border-[var(--error-border)] bg-[var(--surface-0)] px-[var(--space-4)] text-[length:var(--text-form-lg)] font-[var(--weight-medium)] text-[var(--error-text)] hover:bg-[var(--error-bg)] disabled:opacity-60 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
          >
            <Icon name="cancel" size={20} />
            ปฏิเสธ
            <kbd className="ml-[var(--space-1)] rounded-[var(--radius-xs)] border border-[var(--error-border)] px-1 font-[family-name:var(--font-num)] text-[length:var(--text-kbd)]">R</kbd>
          </button>

          {approveConsequence && (
            <p className="ml-[var(--space-2)] flex-1 text-[length:var(--text-table-sm)] leading-[var(--leading-thai)] text-[var(--ink-600)]">
              {approveConsequence}
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-[var(--space-25)] rounded-[var(--radius-md)] border border-[var(--error-border)] bg-[var(--error-bg)] p-[var(--space-3)]">
          <fieldset>
            <legend className="mb-[var(--space-2)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-[var(--error-text)]">
              เหตุผลที่ปฏิเสธ <span className="font-[var(--weight-regular)] text-[var(--ink-700)]">(ลูกค้าจะเห็นข้อความนี้)</span>
            </legend>
            <div className="flex flex-col gap-[var(--space-05)]">
              {reasons.map((r, i) => (
                <label
                  key={r.code}
                  className="flex min-h-[var(--hit-control)] cursor-pointer items-center gap-[var(--space-2)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-form)] leading-[var(--leading-form)] text-[var(--ink-900)] hover:bg-white/60"
                >
                  <input
                    ref={i === 0 ? firstReasonRef : undefined}
                    type="radio"
                    name="reject-reason"
                    value={r.code}
                    checked={code === r.code}
                    onChange={() => setCode(r.code)}
                    className="h-4 w-4 accent-[var(--error-solid)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
                  />
                  <span>{r.label}</span>
                  {r.requiresDetail && (
                    <span className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">(ต้องระบุรายละเอียด)</span>
                  )}
                </label>
              ))}
            </div>
          </fieldset>

          <label className="flex flex-col gap-[var(--space-1)]">
            <span className="text-[length:var(--text-form)] text-[var(--ink-700)]">
              รายละเอียดเพิ่มเติม {needsDetail && <span className="text-[var(--error-text)]">จำเป็น</span>}
            </span>
            <textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              rows={2}
              aria-describedby="reject-detail-hint"
              className="rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] p-[var(--space-2)] font-[family-name:var(--font-thai)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-900)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            />
            <span id="reject-detail-hint" className="text-[length:var(--text-table-sm)] text-[var(--ink-600)]">
              {needsDetail
                ? `ต้องอย่างน้อย ${MIN_DETAIL} ตัวอักษร — พิมพ์แล้ว ${detail.trim().length}`
                : 'เขียนเพิ่มได้ถ้าต้องการอธิบายให้ลูกค้าเข้าใจ'}
            </span>
          </label>

          <div className="flex items-center gap-[var(--space-2)]">
            <button
              type="button"
              disabled={!canSubmit || busy}
              onClick={() => chosen && onReject({ code: chosen.code, label: chosen.label, detail: detail.trim() })}
              className="inline-flex min-h-[var(--hit-primary)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] bg-[var(--error-solid)] px-[var(--space-4)] text-[length:var(--text-form)] font-[var(--weight-semibold)] text-white hover:bg-[var(--error-text)] disabled:cursor-not-allowed disabled:bg-[var(--bg-disabled)] disabled:text-[var(--text-disabled)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            >
              <Icon name="cancel" size={18} />
              ยืนยันการปฏิเสธ
            </button>
            <button
              type="button"
              onClick={() => { setRejecting(false); setCode(''); setDetail(''); }}
              className="min-h-[var(--hit-primary)] rounded-[var(--radius-sm)] px-[var(--space-3)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-[var(--ink-700)] hover:bg-white/60 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            >
              ยกเลิก
            </button>
            {!canSubmit && (
              <span className="text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
                {chosen ? 'กรอกรายละเอียดให้ครบก่อน' : 'เลือกเหตุผลก่อน'}
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

/** เหตุผลปฏิเสธสำเร็จรูปของคิวสลิป — เขียนให้ลูกค้าอ่านรู้เรื่อง (FR-ADM-09) */
export const SLIP_REJECT_REASONS: RejectReason[] = [
  { code: 'unreadable', label: 'รูปสลิปไม่ชัด อ่านยอดเงินหรือวันที่ไม่ได้ กรุณาถ่ายใหม่' },
  { code: 'duplicate', label: 'สลิปนี้ถูกใช้สะสมคะแนนไปแล้ว' },
  { code: 'amount-mismatch', label: 'ยอดในสลิปไม่ตรงกับยอดที่กรอก', requiresDetail: true },
  { code: 'not-eligible', label: 'สินค้าในใบเสร็จไม่ร่วมรายการสะสมคะแนน', requiresDetail: true },
  { code: 'out-of-window', label: 'วันที่ในสลิปเกินกำหนดการส่งคำขอ' },
  { code: 'wrong-document', label: 'เอกสารที่แนบไม่ใช่สลิปหรือใบเสร็จ' },
  { code: 'other', label: 'เหตุผลอื่น', requiresDetail: true },
];
